// Browser SDK disabled - using HTTP API only to avoid CORS/402 errors
// import mixpanel from 'mixpanel-browser';

const MIXPANEL_TOKEN = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN;

// Check if we're on the server side (not used anymore, but kept for reference)
const isServer = typeof window === 'undefined';

export const initMixpanel = () => {
    // Completely disable browser SDK initialization
    // We use server-side HTTP API for all tracking to avoid CORS/402 errors
    // Browser SDK causes issues with session recording (402 Payment Required)
    
    if (!MIXPANEL_TOKEN) {
        console.warn('Mixpanel token is missing! Check your .env file.');
        return;
    }

    // Skip browser SDK initialization - we only use server-side API
    console.log('ℹ️ Mixpanel browser SDK disabled - using server-side API only');
    return;
    
    // OLD CODE - commented out to prevent browser SDK initialization
    /*
    if (typeof window === 'undefined') {
        return;
    }

    try {
    mixpanel.init(MIXPANEL_TOKEN, {
            autocapture: false,
        debug: false,
            track_pageview: false,
        persistence: "localStorage",
            ignore_dnt: true,
            record_sessions_percent: 0,
            record_heatmap_data: false,
        });
        console.log('✅ Mixpanel initialized (browser SDK)');
    } catch (error) {
        console.error('Error initializing Mixpanel:', error);
    }
    */
}

/**
 * Get client IP address from request
 */
const getClientIP = (req: any): string | null => {
    // Try various headers that might contain the real IP
    const forwarded = req.headers?.['x-forwarded-for'];
    const realIP = req.headers?.['x-real-ip'];
    const cfConnectingIP = req.headers?.['cf-connecting-ip']; // Cloudflare
    
    if (forwarded) {
        // x-forwarded-for can contain multiple IPs, take the first one
        return forwarded.split(',')[0].trim();
    }
    if (realIP) {
        return realIP;
    }
    if (cfConnectingIP) {
        return cfConnectingIP;
    }
    if (req.socket?.remoteAddress) {
        return req.socket.remoteAddress;
    }
    
    return null;
};

/**
 * Parse User-Agent to extract Operating System
 */
const parseOperatingSystem = (userAgent: string | undefined): string => {
    if (!userAgent) return 'Unknown';
    
    const ua = userAgent.toLowerCase();
    
    if (ua.includes('windows')) {
        if (ua.includes('windows nt 10.0')) return 'Windows 10/11';
        if (ua.includes('windows nt 6.3')) return 'Windows 8.1';
        if (ua.includes('windows nt 6.2')) return 'Windows 8';
        if (ua.includes('windows nt 6.1')) return 'Windows 7';
        return 'Windows';
    }
    if (ua.includes('mac os x') || ua.includes('macintosh')) {
        return 'macOS';
    }
    if (ua.includes('linux')) {
        return 'Linux';
    }
    if (ua.includes('android')) {
        return 'Android';
    }
    if (ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod')) {
        return 'iOS';
    }
    
    return 'Unknown';
};

/**
 * Track event using Mixpanel HTTP API (for server-side)
 */
const trackEventViaAPI = async (
    eventName: string,
    userId: string,
    properties: Record<string, any>,
    req?: any // Optional request object for IP and User-Agent
) => {
    if (!MIXPANEL_TOKEN) {
        console.warn('Mixpanel token is missing! Cannot track event:', eventName);
        return;
    }

    try {
        // Get IP and User-Agent if request is provided
        let ip: string | null = null;
        let os: string | undefined = undefined;
        
        if (req) {
            ip = getClientIP(req);
            const userAgent = req.headers?.['user-agent'];
            os = parseOperatingSystem(userAgent);
        }

        const eventProperties: Record<string, any> = {
            token: MIXPANEL_TOKEN,
            distinct_id: userId,
            ...properties,
        };

        // Add IP for automatic geolocation (Mixpanel will determine City and Country)
        if (ip) {
            eventProperties.ip = ip;
        }

        // Add Operating System if available
        if (os) {
            eventProperties.$os = os;
            eventProperties.operating_system = os;
        }

        const data = {
            event: eventName,
            properties: eventProperties,
        };

        const response = await fetch('https://api.mixpanel.com/track', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify([data]),
        });

        if (!response.ok) {
            if (response.status === 402) {
                console.warn(`⚠️ Mixpanel API: Payment Required (402). Check your Mixpanel account plan. Event: ${eventName}`);
            } else {
                console.error(`❌ Mixpanel API error: ${response.status} ${response.statusText} for event: ${eventName}`);
            }
        } else {
            console.log(`✅ Mixpanel event tracked: ${eventName} for user ${userId}`);
        }
    } catch (error) {
        console.error('Error tracking event via Mixpanel API:', error);
    }
};

/**
 * Update user profile using Mixpanel HTTP API (for server-side)
 */
const updateUserProfileViaAPI = async (
    userId: string,
    properties: Record<string, any>
) => {
    if (!MIXPANEL_TOKEN) {
        console.warn('Mixpanel token is missing! Cannot update user profile.');
        return;
    }

    try {
        const data = {
            $token: MIXPANEL_TOKEN,
            $distinct_id: userId,
            $set: properties,
        };

        const response = await fetch('https://api.mixpanel.com/engage', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify([data]),
        });

        if (!response.ok) {
            if (response.status === 402) {
                console.warn(`⚠️ Mixpanel Engage API: Payment Required (402). Check your Mixpanel account plan.`);
            } else {
                console.error(`❌ Mixpanel Engage API error: ${response.status} ${response.statusText}`);
            }
        } else {
            console.log(`✅ Mixpanel profile updated for user ${userId}`);
        }
    } catch (error) {
        console.error('Error updating user profile via Mixpanel API:', error);
    }
};

/**
 * Track user sign-up event
 * @param userId - User ID (uid)
 * @param email - User email
 * @param method - Sign-up method ('Email' or 'Google')
 * @param additionalData - Additional properties to track
 * @param req - Optional Next.js request object for IP and User-Agent
 */
export const trackSignUp = async (
    userId: string,
    email: string,
    method: 'Email' | 'Google',
    additionalData?: Record<string, any>,
    req?: any
) => {
    if (!MIXPANEL_TOKEN) {
        console.warn('Mixpanel token is missing! Cannot track sign-up.');
        return;
    }

    try {
        // Always use HTTP API (both server and client) to avoid browser SDK issues
        await updateUserProfileViaAPI(userId, {
            $email: email,
            email: email,
        });

        await trackEventViaAPI('Sign-up', userId, {
            method: method,
            ...additionalData,
        }, req);
    } catch (error) {
        console.error('Error tracking sign-up in Mixpanel:', error);
    }
};

/**
 * Track subscription purchase event
 * @param userId - User ID (uid)
 * @param email - User email
 * @param planId - Plan/Price ID
 * @param amount - Purchase amount
 * @param currency - Currency code (default: 'usd')
 * @param paymentMethod - Payment method ('Stripe' or 'PayPal')
 * @param additionalData - Additional properties to track
 * @param req - Optional Next.js request object for IP and User-Agent
 */
export const trackSubscriptionPurchase = async (
    userId: string,
    email: string,
    planId: string,
    amount: number,
    currency: string = 'usd',
    paymentMethod: 'Stripe' | 'PayPal',
    additionalData?: Record<string, any>,
    req?: any
) => {
    if (!MIXPANEL_TOKEN) {
        console.warn('Mixpanel token is missing! Cannot track subscription purchase.');
        return;
    }

    try {
        // Always use HTTP API (both server and client) to avoid browser SDK issues
        await updateUserProfileViaAPI(userId, {
            $email: email,
            email: email,
            user_plan: 'Premium',
        });

        await trackEventViaAPI('Plan / subscription purchase', userId, {
            plan_id: planId,
            amount: amount,
            currency: currency,
            payment_method: paymentMethod,
            ...additionalData,
        }, req);
    } catch (error) {
        console.error('Error tracking subscription purchase in Mixpanel:', error);
    }
};

/**
 * Track subscription cancellation event
 * @param userId - User ID (uid)
 * @param email - User email
 * @param subscriptionId - Subscription ID
 * @param paymentMethod - Payment method ('Stripe' or 'PayPal')
 * @param additionalData - Additional properties to track
 * @param req - Optional Next.js request object for IP and User-Agent
 */
export const trackSubscriptionCancellation = async (
    userId: string,
    email: string,
    subscriptionId: string,
    paymentMethod: 'Stripe' | 'PayPal',
    additionalData?: Record<string, any>,
    req?: any
) => {
    if (!MIXPANEL_TOKEN) {
        console.warn('Mixpanel token is missing! Cannot track subscription cancellation.');
        return;
    }

    try {
        // Always use HTTP API (both server and client) to avoid browser SDK issues
        await updateUserProfileViaAPI(userId, {
            $email: email,
            email: email,
            user_plan: 'Free',
        });

        await trackEventViaAPI('Subscription cancellation', userId, {
            subscription_id: subscriptionId,
            payment_method: paymentMethod,
            ...additionalData,
        }, req);
    } catch (error) {
        console.error('Error tracking subscription cancellation in Mixpanel:', error);
    }
};